import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ISimOwner } from '@/types/api';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';

const OwnerDetail: React.FC = () => {
  const { iccid } = useParams<{ iccid: string }>();
  const navigate = useNavigate();
  const [owner, setOwner] = useState<ISimOwner | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSeeding, setIsSeeding] = useState(false);
  const [seedMessage, setSeedMessage] = useState<string | null>(null);

  useEffect(() => {
    const fetchOwner = async () => {
      if (!iccid) return;

      try {
        setIsLoading(true);
        const response = await fetch(`/api/v1/sims/${iccid}/owner`);

        if (!response.ok) {
          throw new Error('Failed to fetch owner information');
        }

        const data = await response.json();
        setOwner(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setIsLoading(false);
      }
    };

    fetchOwner();
  }, [iccid]);

  const handleSeed = async () => {
    if (!owner?.owner) {
      setSeedMessage('Cannot seed: Owner name not found');
      return;
    }

    if (!window.confirm(`Seed all SIMs and modems for owner "${owner.owner}" to customer servers?`)) {
      return;
    }

    setIsSeeding(true);
    setSeedMessage(null);

    try {
      // Call the seed API endpoint (TODO: implement backend endpoint)
      const response = await fetch(`/api/v1/seed/${encodeURIComponent(owner.owner)}`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error('Failed to seed data');
      }

      const data = await response.json();
      setSeedMessage(data.message || 'Data seeded successfully');
    } catch (err) {
      setSeedMessage(err instanceof Error ? err.message : 'Failed to seed data');
    } finally {
      setIsSeeding(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <ErrorMessage
        message={error}
        onRetry={() => navigate(-1)}
      />
    );
  }

  if (!owner) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-500 dark:text-gray-400 text-lg mb-4">
          No owner information found for this SIM card
        </div>
        <Link
          to="/app/sims"
          className="text-blue-600 hover:text-blue-800 underline"
        >
          Back to SIM list
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/app/sims"
          className="text-blue-600 hover:text-blue-800 mb-4 inline-block"
        >
          ← Back to SIM list
        </Link>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Owner Information</h1>
      </div>

      {/* Owner Details Card */}
      <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
            {owner.owner || 'Unassigned'}
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
            Owner details for SIM card {owner.iccid}
          </p>
        </div>
        <div className="border-t border-gray-200 dark:border-gray-700">
          <dl>
            {/* SIM Information Section */}
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                SIM Information
              </dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:col-span-2">
              </dd>
            </div>

            <div className="bg-white dark:bg-gray-800 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">ICCID</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:col-span-2 sm:mt-0">
                {owner.iccid}
              </dd>
            </div>

            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">IMSI</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:col-span-2 sm:mt-0">
                {owner.imsi || <span className="text-gray-400 dark:text-gray-500">Not available</span>}
              </dd>
            </div>

            <div className="bg-white dark:bg-gray-800 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">IP Address</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:col-span-2 sm:mt-0">
                {owner.ip_address || <span className="text-gray-400 dark:text-gray-500">Not assigned</span>}
              </dd>
            </div>

            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">IMEI</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:col-span-2 sm:mt-0">
                {owner.imei || <span className="text-gray-400 dark:text-gray-500">Not available</span>}
              </dd>
            </div>

            <div className="bg-white dark:bg-gray-800 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Label</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:col-span-2 sm:mt-0">
                {owner.label || <span className="text-gray-400 dark:text-gray-500">No label</span>}
              </dd>
            </div>

            {/* Owner Information Section */}
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Owner Information
              </dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:col-span-2">
              </dd>
            </div>

            <div className="bg-white dark:bg-gray-800 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Owner Name</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:col-span-2 sm:mt-0">
                {owner.owner || <span className="text-gray-400 dark:text-gray-500 italic">Unassigned</span>}
              </dd>
            </div>

            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Company</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:col-span-2 sm:mt-0">
                {owner.company || <span className="text-gray-400 dark:text-gray-500">-</span>}
              </dd>
            </div>

            <div className="bg-white dark:bg-gray-800 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Email</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:col-span-2 sm:mt-0">
                {owner.email ? (
                  <a href={`mailto:${owner.email}`} className="text-blue-600 hover:text-blue-800">
                    {owner.email}
                  </a>
                ) : (
                  <span className="text-gray-400 dark:text-gray-500">-</span>
                )}
              </dd>
            </div>

            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Phone</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:col-span-2 sm:mt-0">
                {owner.phone ? (
                  <a href={`tel:${owner.phone}`} className="text-blue-600 hover:text-blue-800">
                    {owner.phone}
                  </a>
                ) : (
                  <span className="text-gray-400 dark:text-gray-500">-</span>
                )}
              </dd>
            </div>

            <div className="bg-white dark:bg-gray-800 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Notes</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:col-span-2 sm:mt-0">
                {owner.notes || <span className="text-gray-400 dark:text-gray-500">-</span>}
              </dd>
            </div>

            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Assigned At</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:col-span-2 sm:mt-0">
                {owner.assigned_at || <span className="text-gray-400 dark:text-gray-500">-</span>}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Seed Message */}
      {seedMessage && (
        <div className={`mt-6 p-4 rounded-md ${seedMessage.includes('success') ? 'bg-green-50 text-green-800' : 'bg-yellow-50 text-yellow-800'}`}>
          <p className="text-sm">{seedMessage}</p>
        </div>
      )}

      {/* Actions */}
      <div className="mt-6 flex gap-4">
        <Link
          to={`/sim/${iccid}`}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          View SIM Details
        </Link>
        <button
          onClick={handleSeed}
          disabled={isSeeding || !owner?.owner}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSeeding ? (
            <>
              <LoadingSpinner size="small" />
              <span className="ml-2">Seeding...</span>
            </>
          ) : (
            'Seed to Customers'
          )}
        </button>
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Back
        </button>
      </div>
    </div>
  );
};

export default OwnerDetail;